import {
  isActionCardSnoozed,
  type ActionCardSurface,
} from '@littlebird/action-cards';
import { useStores, type ActionCard } from '@littlebird/sdk';
import { toast } from 'sonner';

import {
  actionCardAnalyticsContext,
  actionCardAnalyticsSnapshot,
  operationId,
  trackCardAction,
} from '@/features/action-cards/analytics';

import { snoozeWhenLabel, type SnoozeOption } from './feedSnooze';

type ActionCardStore = ReturnType<typeof useStores>['actionCards'];

export type CardPinAndSnooze = {
  pinned: boolean;
  snoozed: boolean;
  // Resolves to whether the server took the change; a failure has already shown its toast
  togglePin: () => Promise<boolean>;
  snooze: (option: SnoozeOption) => void;
  unsnooze: () => void;
};

const showFailure = (error: unknown) => {
  console.error('[FeedCard] pin or snooze failed', error);
  toast.error("Couldn't update this card. Try again.");
};

// Pin and snooze write through the card store, which applies them at once and undoes them if the server turns them down
export const cardPinAndSnooze = (
  card: ActionCard,
  actionCards: ActionCardStore,
  surface: ActionCardSurface = 'web_feeds',
  cardExpanded = false
): CardPinAndSnooze => {
  const pinned = !!card.pinnedAt;
  const mutate = (
    action: 'pin' | 'unpin' | 'snooze' | 'unsnooze',
    call: () => Promise<unknown>,
    trigger?: 'undo'
  ) => {
    const context = actionCardAnalyticsContext(surface, {
      card_expanded: cardExpanded,
    });
    const analytics = actionCardAnalyticsSnapshot(card, context);
    const operation = operationId();
    trackCardAction(action, analytics, 'submitted', operation, {
      ...(trigger ? { trigger } : {}),
    });
    return call().then(
      () =>
        trackCardAction(action, analytics, 'succeeded', operation, {
          ...(trigger ? { trigger } : {}),
        }),
      (error: unknown) => {
        trackCardAction(action, analytics, 'failed', operation, {
          ...(trigger ? { trigger } : {}),
          outcomeStatus: 'error',
        });
        throw error;
      }
    );
  };

  return {
    pinned,
    snoozed: isActionCardSnoozed(card, Date.now()),
    togglePin: () =>
      mutate(pinned ? 'unpin' : 'pin', () =>
        actionCards.pin(card.id, !pinned)
      ).then(
        () => true,
        (error: unknown) => {
          showFailure(error);
          return false;
        }
      ),
    snooze: ({ until }) => {
      // The card hides at once, so the toast shows at once too, and goes if the server turns the snooze down
      const toastId = toast(
        `Snoozed until ${snoozeWhenLabel(until, new Date())}`,
        {
          action: {
            label: 'Undo',
            onClick: () => {
              void mutate(
                'unsnooze',
                () => actionCards.snooze(card.id, null),
                'undo'
              ).catch(showFailure);
            },
          },
        }
      );
      void mutate('snooze', () => actionCards.snooze(card.id, until)).catch(
        (error: unknown) => {
          toast.dismiss(toastId);
          showFailure(error);
        }
      );
    },
    unsnooze: () => {
      void mutate('unsnooze', () => actionCards.snooze(card.id, null)).catch(
        showFailure
      );
    },
  };
};

export const useCardPinAndSnooze = (
  card: ActionCard,
  surface: ActionCardSurface = 'web_feeds',
  cardExpanded = false
): CardPinAndSnooze => {
  // React Compiler would cache `card.pinnedAt` on the stable MobX card
  'use no memo';
  const { actionCards } = useStores();
  return cardPinAndSnooze(card, actionCards, surface, cardExpanded);
};
